import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  HostListener,
  ElementRef,
  OnInit,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subject, debounce, timer } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { TextButtonComponent } from '@custom-components/general/button/text-button/text-button.component';

/** A single result row in a `'typeahead'` filter's dropdown. */
export interface TypeaheadItem {
  /**
   * Stable, unique key. This is the value emitted back in `filterChange` for the
   * group, so it must map to whatever your backend filters on. Must be unique
   * across results — duplicate or empty ids collide (every matching row checks).
   */
  id: string;
  /** Primary text shown in the result row and in the active-filter chip. */
  label: string;
  /** Optional muted secondary line beneath the label. */
  sublabel?: string;
}

/**
 * Declarative config for one filter pill. `type` selects one of three
 * behaviours; fields not relevant to the chosen type are ignored.
 *
 * @example Select (default) — dropdown of fixed options
 * ```ts
 * { key: 'status', label: 'Status', options: ['Active', 'Inactive'], selection: 'single' }
 * ```
 * @example Toggle — inline on/off pill (emits `['true']` when on, `[]` when off)
 * ```ts
 * { key: 'showDeleted', label: 'Show deleted', type: 'toggle' }
 * ```
 * @example Typeahead — async, debounced search
 * ```ts
 * { key: 'partner', label: 'Partner', type: 'typeahead', selection: 'single',
 *   search: q => firstValueFrom(api.search(q)).then(r => r.map(x => ({ id: x.id, label: x.name }))) }
 * ```
 */
export interface FilterGroup {
  /** Unique key — used as the key in the `filterChange` record and to track the pill. */
  key: string;
  /** Pill label (already translated). For a `'toggle'` this is also the chip text. */
  label: string;
  /** Pill behaviour. Defaults to `'select'`. */
  type?: 'select' | 'toggle' | 'typeahead';
  /** `'select'` only — the dropdown options. */
  options?: string[];
  /** `'select'` only — display text per option; the option value stays what is emitted and filtered on. */
  optionLabels?: Record<string, string>;
  /** `'select'` + `'typeahead'` — single- or multi-select. Defaults to `'multi'`. */
  selection?: 'single' | 'multi';
  /** `'typeahead'` only — async data source, called (debounced) as the user types. */
  search?: (query: string) => Promise<TypeaheadItem[]>;
  /** `'typeahead'` only — placeholder for the in-dropdown search input. */
  placeholder?: string;
  /** `'typeahead'` only — minimum query length before `search` fires. Defaults to `1`. */
  minChars?: number;
  /** `'typeahead'` only — debounce before `search` fires, in ms. Defaults to `300`. */
  debounceMs?: number;
  /** `'typeahead'` only — looks up the display label for an id that arrives without one (e.g. a deep-linked
   * `initialFilters` value). Until it resolves, or when it fails or is absent, the chip shows the id. */
  resolve?: (id: string) => Promise<TypeaheadItem | null>;
}

@Component({
  selector: 'xemex-table-filter-bar',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslateModule, TextButtonComponent],
  templateUrl: './table-filter-bar.component.html',
  styleUrl: './table-filter-bar.component.scss',
})
/**
 * Reusable filter bar for list/table pages. Renders an optional search box plus
 * a row of filter pills declared via {@link FilterGroup} (select / toggle /
 * typeahead), and an active-filter chip row with per-chip removal and a
 * "Clear filters" action. Project page actions (e.g. an "Add" button) into the
 * trailing `[actions]` content slot.
 *
 * The bar is data-agnostic: it emits the raw selections and never calls a
 * backend itself. For `'typeahead'` groups the consumer supplies the async
 * `search` fn; for everything else the consumer maps the emitted
 * `filterChange` record onto its own query.
 */
export class TableFilterBarComponent implements OnInit {
  /** The filter pills to render, in display order. */
  filterGroups = input<FilterGroup[]>([]);
  /** Placeholder for the free-text search input. */
  searchPlaceholder = input<string>('tableFilterBar.searchPlaceholder');
  /** Whether to show the free-text search input. */
  showSearch = input<boolean>(true);
  /** Pre-selects pills/chips on init (e.g. a filter deep-linked via query params) — only read
   * once, doesn't react to later changes. */
  initialFilters = input<Record<string, string[]>>({});
  /** Starts the search box with this term (e.g. one deep-linked via query params). Only read once,
   * doesn't react to later changes. It does not emit `searchChange`: the consumer already has it. */
  initialSearch = input<string>('');

  /** Emits the debounced search term (empty string when cleared). */
  searchChange = output<string>();
  /** Emits the active filters keyed by `FilterGroup.key`. Toggles emit `['true']`/`[]`; typeaheads emit the selected `TypeaheadItem.id`s. */
  filterChange = output<Record<string, string[]>>();

  protected searchInput = signal(''); // raw text in the field
  protected query = signal(''); // committed term — drives the chip
  protected activeFilters = signal<Record<string, string[]>>({});
  protected openPillKey = signal<string | null>(null);
  protected dropdownPos = signal<{ top: number; left: number; maxHeight: number } | null>(null);

  // Remembers the {id,label} of each typeahead selection so chips can show the
  // human-readable label while activeFilters/filterChange carry only the id.
  protected selectedTypeahead = signal<Record<string, TypeaheadItem[]>>({});

  // Per-open typeahead popover state (only one popover is ever open at a time).
  protected taQuery = signal('');
  protected taResults = signal<TypeaheadItem[]>([]);
  protected taLoading = signal(false);
  protected taActive = signal(0);
  private taReqId = 0;
  private taTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly search$ = new Subject<string>();
  private static readonly SEARCH_DEBOUNCE_MS = 500;

  protected activeChips = computed<{ key: string; value: string; label?: string }[]>(() => {
    const chips: { key: string; value: string; label?: string }[] = [];
    const q = this.query();
    if (q) chips.push({ key: '__search__', value: q });
    const active = this.activeFilters();
    // Iterate the groups (not activeFilters) so chip order mirrors the pill order.
    for (const group of this.filterGroups()) {
      const values = active[group.key] ?? [];
      if (values.length) chips.push(...this.chipsForGroup(group, values));
    }
    return chips;
  });

  private chipsForGroup(
    group: FilterGroup,
    values: string[],
  ): { key: string; value: string; label?: string }[] {
    if (group.type === 'toggle') {
      // A toggle has no per-value labels — show the group label itself as the chip.
      return values.includes('true') ? [{ key: group.key, value: group.label }] : [];
    }
    if (group.type === 'typeahead') {
      // The value is an id; show the remembered human-readable label instead.
      return (this.selectedTypeahead()[group.key] ?? []).map(it => ({
        key: group.key,
        value: it.label,
      }));
    }
    return values.map(v => {
      const label = group.optionLabels?.[v];
      return label ? { key: group.key, value: v, label } : { key: group.key, value: v };
    });
  }

  protected hasActiveFilters = computed(() => this.activeChips().length > 0);

  constructor(private el: ElementRef) {
    // The committed search term (and thus the chip) updates only once the
    // debounce settles, so the chip appears exactly when the search fires.
    this.search$
      .pipe(
        debounce(() => timer(TableFilterBarComponent.SEARCH_DEBOUNCE_MS)),
        takeUntilDestroyed(),
      )
      .subscribe(q => {
        if (q === this.query()) return;
        this.query.set(q);
        this.searchChange.emit(q);
      });

    // A pill can show up after init (e.g. one that depends on the user profile still loading), so an
    // initial value for it is picked up when its group appears. Each key is seeded once only.
    effect(() => {
      this.filterGroups();
      this.initialFilters();
      untracked(() => this.seedInitialFilters());
    });
  }

  ngOnInit(): void {
    this.seedInitialFilters();
    const search = this.initialSearch();
    this.searchInput.set(search);
    this.query.set(search);
  }

  private readonly seededKeys = new Set<string>();

  /** Applies `initialFilters` for every key not seeded yet. A key counts as seeded once its group exists, so
   * later changes to the groups or to the user's own selection are never overwritten. */
  private seedInitialFilters(): void {
    const groups = this.filterGroups();
    const pending = Object.entries(this.initialFilters()).filter(
      ([key]) => !this.seededKeys.has(key),
    );
    if (!pending.length) return;
    this.activeFilters.update(f => ({ ...f, ...Object.fromEntries(pending) }));
    for (const [key, ids] of pending) {
      const group = groups.find(g => g.key === key);
      if (!group) continue;
      this.seededKeys.add(key);
      if (group.type === 'typeahead' && ids.length) this.seedTypeaheadSelection(group, ids);
    }
  }

  /** A typeahead id from `initialFilters` has no remembered label, so chips and chip removal (which both
   * read `selectedTypeahead`) would ignore it. Seed it with the id as label, then upgrade via `resolve`. */
  private seedTypeaheadSelection(group: FilterGroup, ids: string[]): void {
    this.selectedTypeahead.update(s => ({
      ...s,
      [group.key]: ids.map(id => ({ id, label: id })),
    }));
    for (const id of ids) {
      void group
        .resolve?.(id)
        .then(item => {
          if (item) this.applyResolvedLabel(group.key, item);
        })
        .catch(() => undefined);
    }
  }

  private applyResolvedLabel(key: string, item: TypeaheadItem): void {
    this.selectedTypeahead.update(s => ({
      ...s,
      [key]: (s[key] ?? []).map(it => (it.id === item.id ? item : it)),
    }));
  }

  @HostListener('document:mousedown', ['$event'])
  onDocClick(e: MouseEvent): void {
    if (!this.el.nativeElement.contains(e.target)) {
      this.dismissOpenPill();
    }
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  onScrollOrResize(): void {
    this.dismissOpenPill();
  }

  private dismissOpenPill(): void {
    if (this.taTimer) {
      clearTimeout(this.taTimer);
      this.taTimer = null;
    }
    this.taReqId++; // invalidate any in-flight typeahead response
    this.openPillKey.set(null);
  }

  protected onSearch(q: string): void {
    this.searchInput.set(q);
    this.search$.next(q);
  }

  protected togglePill(key: string, event: MouseEvent): void {
    if (this.openPillKey() === key) {
      this.openPillKey.set(null);
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const margin = 8;
    const dropdownWidth = 240;
    const maxHeight = window.innerHeight - rect.bottom - 4 - 64;
    const maxLeft = window.innerWidth - dropdownWidth - margin;
    const left = Math.max(margin, Math.min(rect.left, maxLeft));
    this.dropdownPos.set({ top: rect.bottom + 4, left, maxHeight });
    this.openPillKey.set(key);
  }

  protected isPillOpen(key: string): boolean {
    return this.openPillKey() === key;
  }

  protected countActive(key: string): number {
    return (this.activeFilters()[key] ?? []).length;
  }

  protected isPillActive(key: string): boolean {
    return this.countActive(key) > 0;
  }

  protected isOptionSelected(key: string, option: string): boolean {
    return (this.activeFilters()[key] ?? []).includes(option);
  }

  protected toggleOption(key: string, option: string): void {
    const group = this.filterGroups().find(g => g.key === key);
    const current = this.activeFilters()[key] ?? [];
    const isSingle = group?.selection === 'single';
    if (isSingle && current.includes(option)) return;
    const updated = this.nextSelection(current, option, isSingle);
    this.activeFilters.update(f => ({ ...f, [key]: updated }));
    this.filterChange.emit(this.activeFilters());
    if (isSingle) this.openPillKey.set(null);
  }

  private nextSelection(current: string[], option: string, isSingle: boolean): string[] {
    if (isSingle) return [option];
    if (current.includes(option)) return current.filter(v => v !== option);
    return [...current, option];
  }

  protected isSingleSelect(key: string): boolean {
    return this.filterGroups().find(g => g.key === key)?.selection === 'single';
  }

  protected isToggleOn(key: string): boolean {
    return (this.activeFilters()[key] ?? []).includes('true');
  }

  protected toggleBoolean(key: string): void {
    const next = this.isToggleOn(key) ? [] : ['true'];
    this.activeFilters.update(f => ({ ...f, [key]: next }));
    this.filterChange.emit(this.activeFilters());
  }

  // ── Typeahead ─────────────────────────────────────────────────────────────
  protected toggleTypeahead(group: FilterGroup, event: MouseEvent): void {
    if (this.openPillKey() === group.key) {
      this.closeTypeahead();
      return;
    }
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const margin = 12;
    const width = 300;
    // Left-align by default; flip to right-align if the panel would overflow.
    const flip = rect.left + width > window.innerWidth - margin;
    const left = flip ? Math.max(margin, rect.right - width) : rect.left;
    const maxHeight = window.innerHeight - rect.bottom - 4 - 64;
    this.dropdownPos.set({ top: rect.bottom + 4, left, maxHeight });
    this.taQuery.set('');
    this.taResults.set([]);
    this.taLoading.set(false);
    this.taActive.set(0);
    this.openPillKey.set(group.key);
  }

  private closeTypeahead(): void {
    if (this.taTimer) {
      clearTimeout(this.taTimer);
      this.taTimer = null;
    }
    this.taReqId++; // invalidate any in-flight response
    this.openPillKey.set(null);
  }

  protected onTypeaheadInput(group: FilterGroup, value: string): void {
    this.taQuery.set(value);
    if (this.taTimer) clearTimeout(this.taTimer);
    const term = value.trim();
    const minChars = group.minChars ?? 1;
    if (term.length < minChars) {
      this.taResults.set([]);
      this.taLoading.set(false);
      return;
    }
    this.taLoading.set(true);
    const myId = ++this.taReqId;
    this.taTimer = setTimeout(async () => {
      try {
        const out = (await group.search?.(term)) ?? [];
        if (myId === this.taReqId) {
          this.taResults.set(out);
          this.taActive.set(0);
        }
      } catch {
        if (myId === this.taReqId) this.taResults.set([]);
      } finally {
        if (myId === this.taReqId) this.taLoading.set(false);
      }
    }, group.debounceMs ?? 300);
  }

  protected clearTypeaheadQuery(group: FilterGroup): void {
    this.onTypeaheadInput(group, '');
  }

  protected isTypeaheadSelected(key: string, id: string): boolean {
    return (this.activeFilters()[key] ?? []).includes(id);
  }

  protected selectTypeahead(group: FilterGroup, item: TypeaheadItem): void {
    const isSingle = group.selection !== 'multi';
    const current = this.selectedTypeahead()[group.key] ?? [];
    const already = current.some(it => it.id === item.id);
    let nextItems: TypeaheadItem[];
    if (isSingle) {
      nextItems = already ? [] : [item];
    } else if (already) {
      nextItems = current.filter(it => it.id !== item.id);
    } else {
      nextItems = [...current, item];
    }
    this.selectedTypeahead.update(s => ({ ...s, [group.key]: nextItems }));
    this.activeFilters.update(f => ({ ...f, [group.key]: nextItems.map(it => it.id) }));
    this.filterChange.emit(this.activeFilters());
    if (isSingle) this.closeTypeahead();
  }

  protected onTypeaheadKeydown(group: FilterGroup, event: KeyboardEvent): void {
    const len = this.taResults().length;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.taActive.update(a => Math.min(a + 1, len - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.taActive.update(a => Math.max(a - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const item = this.taResults()[this.taActive()];
      if (item) this.selectTypeahead(group, item);
    } else if (event.key === 'Escape') {
      this.closeTypeahead();
    }
  }

  // Splits a label around the matched query substring for teal-bold highlighting.
  protected highlightParts(
    label: string,
    term: string,
  ): { pre: string; mark: string; post: string } {
    const t = term.trim();
    if (!t) return { pre: label, mark: '', post: '' };
    const idx = label.toLowerCase().indexOf(t.toLowerCase());
    if (idx === -1) return { pre: label, mark: '', post: '' };
    return {
      pre: label.slice(0, idx),
      mark: label.slice(idx, idx + t.length),
      post: label.slice(idx + t.length),
    };
  }

  protected clearPill(key: string): void {
    this.activeFilters.update(f => ({ ...f, [key]: [] }));
    this.filterChange.emit(this.activeFilters());
  }

  protected removeChip(key: string, value: string): void {
    if (key === '__search__') {
      this.searchInput.set('');
      this.query.set('');
      this.searchChange.emit('');
      this.search$.next(''); // supersede any pending debounced term
      return;
    }
    const type = this.filterGroups().find(g => g.key === key)?.type;
    if (type === 'toggle') {
      // A toggle chip carries the group label, not a value — clear it outright.
      this.activeFilters.update(f => ({ ...f, [key]: [] }));
    } else if (type === 'typeahead') {
      // The chip shows a label; drop the matching item(s) and their id(s).
      const removeIds = new Set(
        (this.selectedTypeahead()[key] ?? []).filter(it => it.label === value).map(it => it.id),
      );
      this.selectedTypeahead.update(s => ({
        ...s,
        [key]: (s[key] ?? []).filter(it => !removeIds.has(it.id)),
      }));
      this.activeFilters.update(f => ({
        ...f,
        [key]: (f[key] ?? []).filter(id => !removeIds.has(id)),
      }));
    } else {
      this.activeFilters.update(f => ({ ...f, [key]: (f[key] ?? []).filter(v => v !== value) }));
    }
    this.filterChange.emit(this.activeFilters());
  }

  protected clearAll(): void {
    this.activeFilters.set({});
    this.selectedTypeahead.set({});
    this.filterChange.emit({});
    this.searchInput.set('');
    this.query.set('');
    this.searchChange.emit('');
    this.search$.next(''); // supersede any pending debounced term
  }
}
